import { defineAsyncComponent } from 'vue';
import dayjs from 'dayjs';

const formatDecimal = (value: any, precision = 3) => {
	const num = parseFloat(value);
	if (isNaN(num)) return value ?? '';
	const fixed = num.toFixed(precision);
	return fixed.indexOf('.') === -1 ? fixed : fixed.replace(/0+$/, '').replace(/\.$/, '');
};

const renderStockBalance = (_value: any, row: any) => {
	const packages = parseFloat(row?.quantity) || 0;
	const packageCount = parseFloat(row?.package_count) || 0;
	let units: number | null = null;

	if (row?.balance_units !== undefined && row?.balance_units !== null) {
		const parsed = parseFloat(row.balance_units);
		units = isNaN(parsed) ? null : parsed;
	} else if (packageCount > 0) {
		units = packages * packageCount;
	}

	if (packages === 0 && (units === null || units === 0)) return '';

	const packagesText = packages ? `${formatDecimal(packages)} упак` : '';
	const unitsText = units ? `${formatDecimal(units)} ед` : '';

	if (packagesText && unitsText) return `${packagesText} / ${unitsText}`;
	return packagesText || unitsText || '';
};

export default {
	description: "",
	access: {
		read: true,
		create: true,
		update: true,
		delete: true
	},
	fields: {},
	forms: {
		list: {
			component: defineAsyncComponent(() => import('./forms/List.vue')),
			controller: defineAsyncComponent(() => import('./forms/ListController')),
			config: {
				limits: [10, 20, 30, 40, 50, 100, 300, 1000],
				limit: 10,
				fields: {
					id: {
						config: {
							visible: false,
							hide: true
						}
					},
					position_number: {
						description: "№ п/п",
						config: {
							width: '60px',
							sticky: true,
							visible: true,
							sorting: '"medicament_stock"."position_number"',
							calc: (row: any) => {
								// Используем position_number из данных строки
								const num = row.position_number;
								if (num !== undefined && num !== null && !isNaN(num)) {
									return String(num);
								}
								// Если нет в данных, вернем пустую строку
								// (номер будет вычислен на основе индекса в цикле, если нужно)
								return '';
							}
						}
					},
					product_name: {
						description: "Наименование",
						config: {
							width: '300px',
							sticky: true,
							sorting: '"medicament_stock"."product_name"'
						}
					},
				document_number: {
					description: "Номер документа",
					config: {
						visible: false,
						hide: true
					}
				},
					manufacturer: {
						description: "Производитель",
						config: {
							width: '200px',
							sorting: '"medicament_stock"."manufacturer"'
						}
					},
					country: {
						description: "Страна",
						config: {
							width: '80px',
							sorting: '"medicament_stock"."country"'
						}
					},
					expiry_date: {
						description: "Срок годности",
						config: {
							width: '120px',
							sorting: '"medicament_stock"."expiry_date"',
							render: (value: any) => value ? dayjs(value).format('DD.MM.YYYY') : ''
						}
					},
					batch_number: {
						description: "Серия",
						config: {
							width: '100px',
							sorting: '"medicament_stock"."batch_number"'
						}
					},
					unit_display: {
						description: "Ед.изм уп/нат.ед",
						config: {
							width: '120px',
							sorting: '"medicament_stock"."package_count"',
							render: (value: any) => value || ''
						}
					},
				beginning_balance_packages: {
					description: "Остаток на начало (уп)",
					config: {
						width: '120px',
						sorting: '"medicament_stock"."beginning_balance_packages"',
						render: (value: any) => { const n = parseFloat(value); return (!n || isNaN(n)) ? '' : formatDecimal(n); }
					}
				},
				beginning_balance_units: {
					description: "Остаток на начало (нат.ед)",
					config: {
						width: '150px',
						sorting: '"medicament_stock"."beginning_balance_units"',
						render: (value: any) => { const n = parseFloat(value); return (!n || isNaN(n)) ? '' : formatDecimal(n); }
					}
				},
				beginning_balance_amount: {
					description: "Остаток на начало (сумма)",
					config: {
						width: '150px',
						sorting: '"medicament_stock"."beginning_balance_amount"',
						render: (value: any) => {
							const n = parseFloat(value);
							return (!n || isNaN(n)) ? '' : n.toFixed(2);
						}
					}
				},
				income_units: {
					description: "Приход (нат.ед)",
					config: {
						width: '120px',
						sorting: '"medicament_stock"."income_units"',
						render: (value: any) => { const n = parseFloat(value); return (!n || isNaN(n)) ? '' : formatDecimal(n); }
					}
				},
				income_amount: {
					description: "Сумма прихода",
					config: {
						width: '130px',
						sorting: '"medicament_stock"."income_amount"',
						render: (value: any) => {
							const n = parseFloat(value);
							return (!n || isNaN(n)) ? '' : n.toFixed(2);
						}
					}
				},
				expense_units: {
					description: "Расход (нат.ед)",
					config: {
						width: '120px',
						sorting: '"medicament_stock"."expense_units"',
						render: (value: any) => { const n = parseFloat(value); return (!n || isNaN(n)) ? '' : formatDecimal(n); }
					}
				},
				expense_amount: {
					description: "Сумма расхода",
					config: {
						width: '130px',
						sorting: '"medicament_stock"."expense_amount"',
						render: (value: any) => {
							const n = parseFloat(value);
							return (!n || isNaN(n)) ? '' : n.toFixed(2);
						}
					}
				},
				reserve_units: {
					description: "РЕЗЕРВ (нат.ед)",
					config: {
						width: '120px',
						sorting: '"medicament_stock"."reserve_units"',
						render: (value: any) => { const n = parseFloat(value); return (!n || isNaN(n)) ? '' : formatDecimal(n); }
					}
				},
				ending_balance_packages: {
					description: "Остаток на конец (уп)",
					config: {
						width: '150px',
						sorting: '"medicament_stock"."ending_balance_packages"',
						render: (value: any) => { const n = parseFloat(value); return (!n || isNaN(n)) ? '' : formatDecimal(n); }
					}
				},
				ending_balance_units: {
					description: "Остаток на конец (нат.ед)",
					config: {
						width: '150px',
						sorting: '"medicament_stock"."ending_balance_units"',
						render: (value: any) => { const n = parseFloat(value); return (!n || isNaN(n)) ? '' : formatDecimal(n); }
					}
				},
				ending_balance_amount: {
					description: "Остаток на конец (сумма)",
					config: {
						width: '150px',
						sorting: '"medicament_stock"."ending_balance_amount"',
						render: (value: any) => {
							const n = parseFloat(value);
							return (!n || isNaN(n)) ? '' : n.toFixed(2);
						}
					}
				},
					quantity: {
						description: "Текущий остаток",
						config: {
							width: '180px',
							render: renderStockBalance,
							visible: false
						}
					},
					unit_id: {
						description: "Ед.",
						config: {
							width: '70px',
							visible: false
						}
					},
					purchase_price: {
						description: "Цена",
						config: {
							width: '100px',
							visible: false
						}
					},
					warehouse_id: {
						description: "Склад",
						config: {
							width: '150px',
							visible: false
						}
					},
					nomenclature_number: {
						description: "Ном. номер",
						config: {
							width: '120px',
							visible: false
						}
					},
					created_at: {
						description: "Создано",
						config: {
							visible: false,
							hide: true
						}
					},
					updated_at: {
						description: "Обновлено",
						config: {
							visible: false,
							hide: true
						}
					},
					deletionmark: {
						description: "Пометка удаления",
						config: {
							visible: false,
							hide: true
						}
					}
				}
			}
		},
		edit: {
			panel: {
				width: '90%'
			},
			component: defineAsyncComponent(() => import('./forms/Edit.vue'))
		},
		select: {
			panel: {
				width: '80%',
				height: '600px'
			},
			config: {
				fields: {
					id: {
						config: {
							visible: false
						}
					},
					product_name: {
						description: "Наименование",
						config: {
							width: '300px',
							sticky: true
						}
					},
					batch_number: {
						description: "Серия",
						config: {
							width: '100px'
						}
					},
					manufacturer: {
						description: "Производитель",
						config: {
							width: '200px'
						}
					},
					country: {
						description: "Страна",
						config: {
							width: '150px'
						}
					},
					expiry_date: {
						description: "Срок годности",
						config: {
							width: '120px',
							render: (value: any) => value ? dayjs(value).format('DD.MM.YYYY') : ''
						}
					},
					unit_id: {
						description: "Ед. изм.",
						config: {
							width: '80px'
						}
					},
				package_count: {
					description: "В упаковке",
					config: {
						width: '100px',
						render: (value: any) => { const n = parseFloat(value); return (!n || isNaN(n)) ? '' : formatDecimal(n); }
					}
				},
					quantity: {
						description: "Остаток",
						config: {
							width: '150px',
							render: renderStockBalance
						}
					},
					purchase_price: {
						description: "Цена",
						config: {
							width: '100px',
							render: (value: any) => value ? parseFloat(value).toFixed(2) : ''
						}
					},
					warehouse_id: {
						description: "Склад",
						config: {
							width: '150px',
							visible: false
						}
					},
					nomenclature_number: {
						description: "Ном. номер",
						config: {
							visible: false
						}
					}
				}
			}
		}
	}
}
